// DonateContent.jsx — the Donate page sections (hero, two ways to give,
// corporate sponsorship, co-founder note). Rendered by both the Navigator's
// Donate tab (BrowserWindow) and the standalone shareable /donate page, which
// each add their own surrounding chrome and footer.
import React from "react";
import ChapterDonationChooser from "./ChapterDonationChooser";
import {
  DONATE_HERO,
  GIVING,
  ONE_TIME_GIVING,
  CLUB_MEMBERSHIP,
  SPONSORSHIP,
  SPONSORSHIP_TIERS,
  COFOUNDER_NOTE,
} from "../../data/landingContent";
import "./donate.css";

export default function DonateContent() {
  return (
  <>
    {/* ===== HERO — Magenta with inline impact stats ===== */}
    <section className="mb-block mb-block-magenta" style={{ padding: 0 }}>
      <div className="donate-hero">
        <div style={{
          padding: "var(--s-block-pad-y) var(--s-block-pad-x)",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: "var(--s-stack)"
        }}>
          <div className="mb-section-head" style={{ margin: 0, maxWidth: "48ch" }}>
            <span className="mb-eyebrow" style={{ color: "var(--mb-butter)" }}>{DONATE_HERO.eyebrow}</span>
            <h2 className="mb-h1" style={{ color: "var(--mb-chalk)" }}>
              {DONATE_HERO.headline.pre}<em className="mb-key" style={{ color: "var(--mb-butter)" }}>{DONATE_HERO.headline.key}</em>{DONATE_HERO.headline.post}
            </h2>
            <p className="mb-lede" style={{ color: "var(--mb-chalk)", opacity: 0.92, marginTop: 12 }}>
              {DONATE_HERO.lede}
            </p>
          </div>

          <div style={{
            background: "var(--mb-ink)",
            border: "2px solid var(--mb-chalk)",
            padding: "20px 24px",
            display: "flex",
            flexDirection: "column",
            gap: 4,
            alignSelf: "flex-start",
            maxWidth: "320px",
          }}>
            <span className="mb-eyebrow" style={{ color: "var(--mb-butter)" }}>{DONATE_HERO.stat.eyebrow}</span>
            <span className="mb-numeral" style={{ fontSize: "var(--tc-data-lg)", fontWeight: 500, color: "var(--mb-chalk)", lineHeight: 1 }}>
              {DONATE_HERO.stat.figure}
            </span>
            <span className="mb-body" style={{ color: "var(--mb-chalk)", opacity: 0.85, marginTop: 8, fontSize: 14 }}>
              {DONATE_HERO.stat.caption}
            </span>
          </div>
        </div>

        <div className="donate-hero__media">
          <img
            src="/assets/support-hero.webp"
            alt="Support Good Neighbor Fund: fueling belief capital for neighborhood founders"
            width="800"
            height="600"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            style={{ width: "100%", height: "100%", objectFit: "cover", position: "absolute", inset: 0 }}
          />
        </div>
      </div>
    </section>

    {/* ===== TWO PATHS — Paper ===== */}
    <section className="mb-block mb-block-paper">
      <div className="mb-section-head" style={{ marginBottom: 32, maxWidth: "56ch" }}>
        <span className="mb-eyebrow" style={{ color: "var(--mb-magenta)" }}>{GIVING.eyebrow}</span>
        <h2 className="mb-h2">
          {GIVING.headline}
        </h2>
      </div>

      <div className="mb-grid mb-grid-2" style={{ gap: 24, alignItems: "stretch" }}>
        {/* ---- One-time ---- */}
        <article className="mb-card" style={{ padding: 0, display: "flex", flexDirection: "column" }}>
          <div style={{
            background: "var(--mb-aqua-soft)",
            borderBottom: "2px solid var(--mb-ink)",
            padding: "16px 24px 14px",
          }}>
            <span className="mb-eyebrow" style={{ color: "var(--mb-aqua-deep)" }}>{ONE_TIME_GIVING.eyebrow}</span>
            <h3 className="mb-h3" style={{ margin: "6px 0 0" }}>{ONE_TIME_GIVING.headline}</h3>
          </div>

          <div style={{ padding: "24px", display: "flex", flexDirection: "column", gap: 18, flex: 1 }}>
            <p className="mb-body" style={{ margin: 0 }}>
              {ONE_TIME_GIVING.body}
            </p>

            <div style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              padding: "14px 16px",
              background: "var(--mb-paper-deep)",
              border: "1px solid var(--mb-ink)",
            }}>
              <span className="mb-eyebrow" style={{ color: "var(--mb-ink)", width: "100%", marginBottom: 4 }}>
                {ONE_TIME_GIVING.amountsLabel}
              </span>
              {ONE_TIME_GIVING.amounts.map((amt) => (
                <span
                  key={amt}
                  className="mb-numeral"
                  style={{
                    padding: "4px 10px",
                    background: "var(--mb-chalk)",
                    border: "1px solid var(--mb-ink)",
                    fontSize: "var(--tc-data-sm)",
                    fontWeight: 500,
                    color: "var(--mb-ink)",
                  }}
                >
                  {amt}
                </span>
              ))}
            </div>

            <div style={{ marginTop: "auto" }}>
              <ChapterDonationChooser
                accent="aqua"
                renderTrigger={(onClick) => (
                  <button
                    type="button"
                    onClick={onClick}
                    className="mb-btn mb-btn-ink mb-btn-full"
                  >
                    {ONE_TIME_GIVING.cta}
                    <span className="mb-btn-arrow" aria-hidden="true">&rarr;</span>
                  </button>
                )}
              />
            </div>
          </div>
        </article>

        {/* ---- Club Membership ---- */}
        <article className="mb-card" style={{ padding: 0, display: "flex", flexDirection: "column", position: "relative" }}>
          <div style={{
            position: "absolute",
            top: -10,
            right: 16,
            background: "var(--mb-magenta)",
            color: "var(--mb-chalk)",
            padding: "4px 10px",
            fontFamily: "var(--font-chrome)",
            fontSize: "var(--tc-label)",
            fontWeight: 700,
            letterSpacing: "var(--track-label)",
            textTransform: "none",
            border: "1px solid var(--mb-ink)",
            zIndex: 1,
          }}>
            {CLUB_MEMBERSHIP.badge}
          </div>

          <div style={{
            background: "var(--mb-grape-soft)",
            borderBottom: "2px solid var(--mb-ink)",
            padding: "16px 24px 14px",
          }}>
            <span className="mb-eyebrow" style={{ color: "var(--mb-grape-deep)" }}>{CLUB_MEMBERSHIP.eyebrow}</span>
            <h3 className="mb-h3" style={{ margin: "6px 0 0" }}>{CLUB_MEMBERSHIP.headline}</h3>
          </div>

          <div style={{ padding: "24px", display: "flex", flexDirection: "column", gap: 18, flex: 1 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
              <span className="mb-numeral" style={{ fontSize: "var(--tc-data-lg)", fontWeight: 500, color: "var(--mb-grape-deep)", lineHeight: 1 }}>
                {CLUB_MEMBERSHIP.price}
              </span>
              <span className="mb-numeral" style={{ fontSize: "var(--tc-data-sm)", color: "var(--mb-ink-60)" }}>
                {CLUB_MEMBERSHIP.priceUnit}
              </span>
            </div>

            <p className="mb-body" style={{ margin: 0 }}>
              Join a community of neighbors who fund the next generation of founders. A steady
              $25/month keeps grants flowing all year. 100% of dues go to your chapter's micro-grant program.
            </p>

            <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 8 }}>
              {CLUB_MEMBERSHIP.benefits.map(benefit => (
                <li key={benefit} className="mb-body" style={{ position: "relative", paddingLeft: 22, fontSize: 14, lineHeight: 1.45 }}>
                  <span style={{
                    position: "absolute",
                    left: 0,
                    top: 6,
                    width: 10,
                    height: 10,
                    background: "var(--mb-grape)",
                    border: "1px solid var(--mb-ink)",
                  }} />
                  {benefit}
                </li>
              ))}
            </ul>

            <a
              href={CLUB_MEMBERSHIP.stripeUrl}
              target="_blank"
              rel="noreferrer"
              className="mb-btn mb-btn-ink mb-btn-full"
              style={{ marginTop: "auto" }}
            >
              {CLUB_MEMBERSHIP.cta}
              <span className="mb-btn-arrow" aria-hidden="true">&rarr;</span>
            </a>
          </div>
        </article>
      </div>

      {/* Tax info — subtle footnote strip inside same paper block */}
      <div style={{
        marginTop: 32,
        padding: "16px 20px",
        border: "1px solid var(--mb-ink)",
        background: "var(--mb-paper-deep)",
        display: "flex",
        alignItems: "center",
        gap: 18,
        flexWrap: "wrap",
      }}>
        <span className="mb-badge">Tax-deductible</span>
        <p className="mb-body" style={{ margin: 0, flex: 1, minWidth: "280px", fontSize: 13, lineHeight: 1.5 }}>
          Donations are tax-deductible to the extent allowed by law. All transactions processed through our
          fiscal sponsor, <strong style={{ fontWeight: 700 }}>BootSector</strong>, a registered 501(c)3 non-profit
          <span className="mb-numeral"> (EIN 85-4082950)</span>. You'll receive a donation receipt where applicable.
        </p>
      </div>
    </section>

    {/* ===== CORPORATE SPONSORSHIP — Aqua with tier cards ===== */}
    <section className="mb-block mb-block-aqua">
      <div className="mb-section-head" style={{ marginBottom: 32, maxWidth: "60ch" }}>
        <span className="mb-eyebrow" style={{ color: "var(--mb-ink)" }}>{SPONSORSHIP.eyebrow}</span>
        <h2 className="mb-h2">
          {SPONSORSHIP.headline.line1}<br/>{SPONSORSHIP.headline.line2}
        </h2>
        <p className="mb-body" style={{ marginTop: 12, maxWidth: "56ch" }}>
          Three tiers of support for businesses that want to plant flags alongside the founders
          in their community. All sponsorships are tax-deductible through our fiscal sponsor.
        </p>
      </div>

      <div className="mb-grid mb-grid-3" style={{ gap: 16, marginBottom: 32 }}>
        {SPONSORSHIP_TIERS.map((tier, i) => (
          <article key={tier.tier} className="mb-card" style={{ padding: 20, display: "flex", flexDirection: "column", gap: 10 }}>
            <span className="mb-numeral" style={{ fontSize: "var(--tc-data-sm)", color: "var(--mb-magenta)", fontWeight: 500, letterSpacing: "var(--track-label)" }}>
              Tier 0{i + 1}
            </span>
            <h3 className="mb-h3" style={{ margin: 0 }}>
              {tier.tier}
            </h3>
            <span className="mb-numeral" style={{ fontSize: "var(--tc-data-lg)", fontWeight: 500, color: "var(--mb-ink)" }}>
              {tier.amount}
            </span>
            <p className="mb-body" style={{ margin: 0, fontSize: 13, lineHeight: 1.45 }}>
              {tier.summary}
            </p>
          </article>
        ))}
      </div>

      <div style={{ textAlign: "center" }}>
        <button
          type="button"
          onClick={() => window.location.href = SPONSORSHIP.mailto}
          className="mb-btn mb-btn-ink"
        >
          {SPONSORSHIP.cta}
          <span className="mb-btn-arrow" aria-hidden="true">&rarr;</span>
        </button>
      </div>
    </section>

    {/* ===== CO-FOUNDER NOTE — Butter ===== */}
    <section className="mb-block mb-block-butter">
      <div className="donate-note">
        <img
          src={COFOUNDER_NOTE.image.src}
          alt={COFOUNDER_NOTE.image.alt}
          width="280"
          height="280"
          loading="lazy"
          decoding="async"
          style={{
            width: "100%",
            aspectRatio: "1 / 1",
            objectFit: "cover",
            display: "block",
            border: "var(--border-ink-2)",
            filter: "grayscale(100%)",
          }}
        />
        <div>
          <span className="mb-eyebrow" style={{ color: "var(--mb-magenta)", display: "block", marginBottom: 20 }}>
            {COFOUNDER_NOTE.eyebrow}
          </span>
          <blockquote
            className="mb-display"
            style={{
              fontSize: "var(--tc-head)",
              lineHeight: 1.35,
              margin: 0,
              borderLeft: "4px solid var(--mb-magenta)",
              paddingLeft: 24,
            }}
          >
            {COFOUNDER_NOTE.quote}
          </blockquote>
          <div style={{ marginTop: 24, paddingLeft: 28, display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ width: 20, height: 2, background: "var(--mb-ink)", display: "inline-block" }} />
            <div>
              <div style={{ fontFamily: "var(--font-content)", fontWeight: 700, fontSize: 15, color: "var(--mb-ink)" }}>
                {COFOUNDER_NOTE.name}
              </div>
              <div className="mb-numeral" style={{ fontSize: 11, color: "var(--mb-ink-60)" }}>
                {COFOUNDER_NOTE.role}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </>
  );
}
